import { type FC, useEffect, useState } from "react";
import type { Connection } from "@/models/connection.ts";
import type { DataGraph, GraphItem } from "@/models/dataGraph.ts";
import type {
	ApplicationLink,
	Instance,
	ServiceGraphDto,
	WebAppInformation,
} from "@/models/healthGraph.ts";
import OrgChartComponent from "./healthOrgChart.tsx";
import PanelDetails from "./panelDetails.tsx";
import { HealthService } from "@/services/HealthService.ts";

export type HealthTreeProps = {
	service: ServiceGraphDto;
	showConnection: boolean;
	depthLimit: number;
	webAppInformation: WebAppInformation;
	currentEnvironment: string;
	refreshServiceData: () => void;
};

let indexEltCount = 0;

const HealthTree: FC<HealthTreeProps> = ({
	service,
	showConnection,
	depthLimit,
	webAppInformation,
	currentEnvironment,
	refreshServiceData,
}: HealthTreeProps) => {
	const [connections, setConnections] = useState<Connection[]>();
	const [data, setData] = useState<DataGraph[]>();
	const [depth, setDepth] = useState<number>();
	const [dataGraph, setDataGraph] = useState<DataGraph | null>(null);

	const ParseChild = (
		parent: DataGraph,
		dataGraphs: Map<string, DataGraph>,
		child: ServiceGraphDto,
		alreadyAddedName: string[],
		connections: Connection[],
		depth: number,
	) => {
		const parentId: string = parent.id;
		const item = dataGraphs.get(child.key) ?? ({} as DataGraph);
		// parent.haveMoreChild = false;
		// if (depthLimit <= depth) {
		// 	parent.haveMoreChild = true;
		// 	return;
		// }

		//item.id = child.key+"_"+indexEltCount++;
		item.id = child.key;
		item.key = child.key;
		item.parentId = parentId;
		item.name = child.name;
		item.status = child.status;
		item.lifecycle = child.apiDeclaration?.lifeCycle ?? '';
		item.service = child;

		if (child.healthUri != null) {
			item.healthUri = child.healthUri;
		}

		item.type = GetType(child.type);
		item.dataType = child.type;
		item.problems =
			child.problem != null && child.problem?.length > 0
				? child.problem.map((t) => t.detail).join(",")
				: "";
		item.links = [];
		item.checks = [];
		child.childrenLinks?.map((link: string) => {
			connections.push({ from: item.id, to: link });
		});
		child.applicationLinks?.map((link: ApplicationLink) => {
			item.links.push({
				name: link.name,
				icon: getLinkIcon(link.type),
				link: link.url,
				environment: link.environment,
			});
		});
		item.nodes = [];
		child.instances?.map((instance: Instance) => {
			item.nodes?.push(instance.status);
		});
		if (!dataGraphs.has(item.id)) {
			dataGraphs.set(item.id, item);
		}

		child.children?.map((_child) => {
			if (
				_child.type.toLocaleLowerCase() !== "api" &&
				_child.type.toLocaleLowerCase() !== "sg apim" &&
				!_child.type.toLocaleLowerCase().startsWith("azure")
			) {
				_child.icon = GetType(_child.type).replace(
					".",
					`_${_child.status.toLowerCase()}.`,
				);
				item.checks?.push(_child);
			} else {
				ParseChild(
					item,
					dataGraphs,
					_child,
					alreadyAddedName,
					connections,
					depth + 1,
				);
			}
		});
	};
	
    const formatHealth = (str) => {
        if (str == null)
            str = "Undefined";
        else if (str.toLowerCase() == "up")
            str = "healthy";
        else if (str.toLowerCase() == "down")
            str = "unhealthy";
        return str.charAt(0).toUpperCase() + str.slice(1);
        }

	const ComputeDataGraph = (service: ServiceGraphDto) => {
		const depth: number = 1;
		const alreadyAddedName: string[] = [];
		const connections: Connection[] = [];
		const dataGraphs = new Map<string, DataGraph>();
		const item = dataGraphs.get(service.key) ?? ({} as DataGraph);
		if (service != null) {
			item.id = service.key;
			alreadyAddedName.push(service.name);
			item.key = service.key;
			item.name = service.name;
			item.status = service.status;

			item.service = service;
			item.problems =
				service.problem != null && service.problem?.length > 0
					? service.problem.map((t) => t.detail).join(",")
					: "";
			item.type = GetType(service.type);
			item.links = [];
			item.nodes = [];
			item.checks = [];
			service.applicationLinks?.map((link: ApplicationLink) => {
				item.links.push({
					name: link.type,
					icon: getLinkIcon(link.type),
					link: link.url,
					environment: link.environment,
				});
			});
			if (!dataGraphs.has(item.id)) {
				dataGraphs.set(item.id, item);
			}

			service.children?.map((child: ServiceGraphDto) => {
				ParseChild(
					item,
					dataGraphs,
					child,
					alreadyAddedName,
					connections,
					depth,
				);
			});
			setData(Array.from(dataGraphs.values()));
			setDepth(depth);
			setConnections(connections);
		}
	};

	const GetType = (type: string) => {
		type = type.toLocaleLowerCase();
		if (type === "web-apps") {
			return "/chart/cdn.png";
		}
		if (type === "website") {
			return "/chart/cdn.png";
		}
		if (type === "api") {
			return "/chart/api.png";
		}
		if (type === "database") {
			return "/chart/db.png";
		}
		if (type === "azure frontdoor") {
			return "/chart/frontdoor.svg";
		}
		if (type === "azure apim") {
			return "/chart/apim.svg";
		}
		if (type === "azure apim lan") {
			return "/chart/apim.svg";
		}
		if (type === "azure app gateway") {
			return "/chart/appgateway.svg";
		}
		return "/chart/health.svg";
	};

	const getLinkIcon = (type: string) => {
		switch (type) {
			case "do-it-now":
				return "din.png";
			default:
				return type + ".svg";
		}
	};

	const onNodeClick = (d: any) => {
		setDataGraph(d.data);
	};
	const closePanel = (): void => {
		setDataGraph(null);
	};
	const refreshData = (): void => {
		refreshServiceData();
	};

	useEffect(() => {
		depthLimit ??= 9999;
		ComputeDataGraph(service);
	}, [service, depthLimit]);

	return (
		<>
			<PanelDetails
				service={dataGraph?.service}
				webAppInformation={webAppInformation}
				closePanel={closePanel}
				currentEnvironment={currentEnvironment}
				refreshData={refreshData}
			/>
			{data != null ? (
				<OrgChartComponent
					data={data}
					onNodeClick={onNodeClick}
					currentEnvironment={currentEnvironment}
					connections={connections!}
					showConnection={showConnection}
					depthLimit={depthLimit}
				/>
			) : null}
		</>
	);
};

export default HealthTree;